import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Input,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import { cotizacionesService } from '../../services/cotizacionesService';
import { sucursalesService } from '../../services/sucursalesService';
import { empresasService } from '../../services/empresasService';
import { useAuth } from '../../contexts/AuthContext';
import CotizacionForm from './CotizacionForm';
import CotizacionDetalle from './CotizacionesDetalle';
import SnowFallComponent from 'components/SnowFallComponent';

function Cotizaciones() {
  const { user } = useAuth();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDetalle, setShowDetalle] = useState(false);
  const [cotizacionSeleccionada, setCotizacionSeleccionada] = useState(null);
  const [formKey, setFormKey] = useState(0); // Key para forzar remontaje del formulario
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');

  useEffect(() => {
    loadEmpresaYSucursales();
  }, []);

  useEffect(() => {
    if (!loadingSucursales && sucursalSeleccionada) {
      loadVentas();
    }
  }, [sucursalSeleccionada, filtroCliente, filtroFechaDesde, filtroFechaHasta, filtroEstatus, loadingSucursales]);

  // Verificar si hay un ID en sessionStorage para abrir el detalle automáticamente
  useEffect(() => {
    const openDetailId = sessionStorage.getItem('openDetail_cotizacion');
    if (openDetailId && !loading && sucursalSeleccionada) {
      // Cargar la cotización específica desde la API para asegurar que esté disponible
      // independientemente del filtro de sucursal
      const loadCotizacionFromAPI = async () => {
        try {
          const response = await cotizacionesService.getById(openDetailId);
          if (response.success && response.data) {
            sessionStorage.removeItem('openDetail_cotizacion');
            handleView(response.data);
          } else {
            // Si no se encuentra en la API, intentar buscarla en la lista actual
            sessionStorage.removeItem('openDetail_cotizacion');
            const cotizacion = cotizaciones.find(c => 
              (c.CotizacionID || c.cotizacionID)?.toString() === openDetailId
            );
            if (cotizacion) {
              handleView(cotizacion);
            }
          }
        } catch (error) {
          // Si hay error, intentar buscarla en la lista actual como fallback
          sessionStorage.removeItem('openDetail_cotizacion');
          const cotizacion = cotizaciones.find(c => 
            (c.CotizacionID || c.cotizacionID)?.toString() === openDetailId
          );
          if (cotizacion) {
            handleView(cotizacion);
          }
        }
      };
      
      loadCotizacionFromAPI();
    }
  }, [loading, cotizaciones, sucursalSeleccionada]);

  const loadEmpresaYSucursales = async () => {
    try {
      setLoadingSucursales(true);
      const empresasResponse = await empresasService.getAll();
      if (empresasResponse.success && empresasResponse.data) {
        const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        if (empresaActiva) {
          setEmpresa(empresaActiva);
          const sucursalesResponse = await sucursalesService.getAll(empresaActiva.EmpresaID || empresaActiva.empresaID);
          if (sucursalesResponse.success && sucursalesResponse.data) {
            const sucursalesData = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
            const normalized = sucursalesData.map(suc => ({
              SucursalID: suc.SucursalID || suc.sucursalID,
              Nombre: suc.Nombre || suc.nombre || '',
            }));
            setSucursales(normalized);
            if (normalized.length > 0) {
              setSucursalSeleccionada(normalized[0].SucursalID.toString());
            }
          }
        }
      }
    } catch (err) {
      setError('Error al cargar datos de la empresa');
    } finally {
      setLoadingSucursales(false);
    }
  };

  const loadVentas = async () => {
    try {
      setLoading(true);
      setError('');
      const sucursalId = parseInt(sucursalSeleccionada);
      if (!sucursalId) return;

      const clienteId = filtroCliente ? parseInt(filtroCliente) : null;

      const response = await cotizacionesService.getAll(
        sucursalId,
        clienteId,
        filtroFechaDesde || null,
        filtroFechaHasta || null,
        filtroEstatus || null
      );

      if (response.success && response.data) {
        const ventasData = Array.isArray(response.data) ? response.data : [];
        const normalized = ventasData.map(venta => ({
          CotizacionID: venta.CotizacionID || venta.cotizacionID || venta.VentaID || venta.ventaID,
          VentaID: venta.CotizacionID || venta.cotizacionID || venta.VentaID || venta.ventaID, // Para compatibilidad
          SucursalID: venta.SucursalID || venta.sucursalID,
          SucursalNombre: venta.SucursalNombre || venta.sucursalNombre || '',
          ClienteID: venta.ClienteID || venta.clienteID,
          ClienteNombre: venta.ClienteNombre || venta.clienteNombre,
          UsuarioID: venta.UsuarioID || venta.usuarioID,
          UsuarioNombre: venta.UsuarioNombre || venta.usuarioNombre || '',
          Folio: venta.Folio || venta.folio,
          Subtotal: venta.Subtotal !== undefined ? parseFloat(venta.Subtotal) : (venta.subtotal !== undefined ? parseFloat(venta.subtotal) : 0),
          DescuentoTotal: venta.DescuentoTotal !== undefined ? parseFloat(venta.DescuentoTotal) : (venta.descuentoTotal !== undefined ? parseFloat(venta.descuentoTotal) : 0),
          Impuestos: venta.Impuestos !== undefined ? parseFloat(venta.Impuestos) : (venta.impuestos !== undefined ? parseFloat(venta.impuestos) : 0),
          Total: venta.Total !== undefined ? parseFloat(venta.Total) : (venta.total !== undefined ? parseFloat(venta.total) : 0),
          Estatus: venta.Estatus || venta.estatus || '',
          FechaCotizacion: venta.FechaCotizacion || venta.fechaCotizacion || venta.FechaVenta || venta.fechaVenta,
          Observaciones: venta.Observaciones || venta.observaciones,
          VentaID: venta.VentaID || venta.ventaID,
        }));
        setCotizaciones(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar cotizaciones');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCotizacionSeleccionada(null);
    setFormKey(prev => prev + 1); // Incrementar key para forzar remontaje del formulario
    setShowForm(true);
  };

  const handleView = async (cotizacion) => {
    try {
      setLoading(true);
      const response = await cotizacionesService.getById(cotizacion.CotizacionID || cotizacion.cotizacionID || cotizacion.VentaID || cotizacion.ventaID);
      if (response.success && response.data) {
        setCotizacionSeleccionada(response.data);
        setShowDetalle(true);
      } else {
        setError(response.Message || response.message || 'Error al cargar el detalle de la cotización');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSuccess('Cotización creada exitosamente');
    setTimeout(() => setSuccess(''), 3000);
    loadVentas();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setFormKey(prev => prev + 1); // Incrementar key para resetear el formulario la próxima vez
  };

  const handleDetalleClose = () => {
    setShowDetalle(false);
    setCotizacionSeleccionada(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEstatusBadge = (estatus) => {
    const estatusLower = (estatus || '').toLowerCase();
    if (estatusLower === 'completada') {
      return <Badge color="info">Completada</Badge>;
    } else if (estatusLower === 'convertida') {
      return <Badge color="success">Convertida</Badge>;
    } else if (estatusLower === 'cancelada') {
      return <Badge color="danger">Cancelada</Badge>;
    }
    return <Badge color="secondary">{estatus || 'Desconocido'}</Badge>;
  };

  const exportarCotizacionesCSV = () => {
    if (!cotizaciones || cotizaciones.length === 0) {
      setError('No hay cotizaciones para exportar');
      return;
    }

    const columnas = [
      { label: 'Folio', accessor: 'Folio', render: (v) => v || '-' },
      { label: 'Fecha', accessor: 'FechaCotizacion', render: (v) => formatDate(v) },
      { label: 'Cliente', accessor: 'ClienteNombre', render: (v) => v || 'Cliente General' },
      { label: 'Vendedor', accessor: 'UsuarioNombre' },
      { label: 'Subtotal', accessor: 'Subtotal', render: (v) => formatCurrency(v) },
      { label: 'Impuestos', accessor: 'Impuestos', render: (v) => formatCurrency(v) },
      { label: 'Total', accessor: 'Total', render: (v) => formatCurrency(v) },
      { label: 'Estatus', accessor: 'Estatus' },
    ];

    // Crear encabezados
    const headers = columnas.map(col => col.label).join(',');
    
    // Crear filas
    const rows = cotizaciones.map(item => {
      return columnas.map(col => {
        const value = col.render ? col.render(item[col.accessor], item) : (item[col.accessor] || '');
        // Escapar comillas y envolver en comillas si contiene comas
        const stringValue = String(value).replace(/"/g, '""');
        return stringValue.includes(',') ? `"${stringValue}"` : stringValue;
      }).join(',');
    });

    // Combinar todo
    const csvContent = [headers, ...rows].join('\n');
    
    // Crear blob y descargar
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `cotizaciones_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportarCotizacionDetalleCSV = async (cotizacion) => {
    try {
      setLoading(true);
      const response = await cotizacionesService.getById(cotizacion.CotizacionID || cotizacion.cotizacionID);
      if (!response.success || !response.data) {
        setError('Error al cargar el detalle de la cotización');
        return;
      }

      const detalle = response.data;
      const folio = detalle.Folio || detalle.folio || `COT-${detalle.CotizacionID || detalle.cotizacionID}`;
      
      // Crear contenido CSV
      let csvContent = 'COTIZACIÓN\n';
      csvContent += `Folio,${folio}\n`;
      csvContent += `Fecha,${formatDate(detalle.FechaCotizacion || detalle.fechaCotizacion)}\n`;
      csvContent += `Cliente,${detalle.ClienteNombre || detalle.clienteNombre || 'Cliente General'}\n`;
      csvContent += `Vendedor,${detalle.UsuarioNombre || detalle.usuarioNombre}\n`;
      csvContent += `Sucursal,${detalle.SucursalNombre || detalle.sucursalNombre}\n`;
      csvContent += `Estatus,${detalle.Estatus || detalle.estatus}\n`;
      csvContent += `Subtotal,${formatCurrency(detalle.Subtotal || detalle.subtotal || 0)}\n`;
      csvContent += `Descuento Total,${formatCurrency(detalle.DescuentoTotal || detalle.descuentoTotal || 0)}\n`;
      csvContent += `Impuestos,${formatCurrency(detalle.Impuestos || detalle.impuestos || 0)}\n`;
      csvContent += `Total,${formatCurrency(detalle.Total || detalle.total || 0)}\n`;
      if (detalle.Observaciones || detalle.observaciones) {
        csvContent += `Observaciones,"${(detalle.Observaciones || detalle.observaciones).replace(/"/g, '""')}"\n`;
      }
      csvContent += '\n';
      csvContent += 'DETALLES DE PRODUCTOS\n';
      csvContent += 'Producto,SKU,Cantidad,Precio Unitario,Descuento,Impuestos,Subtotal,Total\n';
      
      const detalles = detalle.Detalles || detalle.detalles || [];
      detalles.forEach(det => {
        const producto = (det.ProductoNombre || det.productoNombre || '').replace(/"/g, '""');
        const sku = (det.ProductoSKU || det.productoSKU || '').replace(/"/g, '""');
        const cantidad = det.Cantidad !== undefined ? det.Cantidad : (det.cantidad || 0);
        const precioUnit = det.PrecioUnitario !== undefined ? det.PrecioUnitario : (det.precioUnitario || 0);
        const descuento = det.Descuento !== undefined ? det.Descuento : (det.descuento || 0);
        const impuestos = det.Impuestos !== undefined ? det.Impuestos : (det.impuestos || 0);
        const subtotal = det.Subtotal !== undefined ? det.Subtotal : (det.subtotal || 0);
        const total = det.Total !== undefined ? det.Total : (det.total || 0);
        
        csvContent += `"${producto}","${sku}",${cantidad},${precioUnit},${descuento},${impuestos},${subtotal},${total}\n`;
      });

      // Crear blob y descargar
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `cotizacion_${folio}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(err.message || 'Error al exportar la cotización');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      key: 'folio',
      label: 'Folio',
      accessor: 'Folio',
      render: (value) => value || '-',
    },
    {
      key: 'fechaCotizacion',
      label: 'Fecha',
      accessor: 'FechaCotizacion',
      render: (value) => formatDate(value),
    },
    {
      key: 'cliente',
      label: 'Cliente',
      accessor: 'ClienteNombre',
      render: (value) => value || 'Cliente General',
    },
    {
      key: 'usuario',
      label: 'Vendedor',
      accessor: 'UsuarioNombre',
    },
    {
      key: 'subtotal',
      label: 'Subtotal',
      accessor: 'Subtotal',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'impuestos',
      label: 'Impuestos',
      accessor: 'Impuestos',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'total',
      label: 'Total',
      accessor: 'Total',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'estatus',
      label: 'Estatus',
      accessor: 'Estatus',
      render: (value) => getEstatusBadge(value),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, row) => {
        return (
          <div>
            <Button
              color="info"
              size="sm"
              className="mr-2"
              onClick={(e) => {
                e.stopPropagation();
                handleView(row);
              }}
              title="Ver Detalle"
            >
              <i className="tim-icons icon-zoom-split" />
            </Button>
            <Button
              color="success"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                exportarCotizacionDetalleCSV(row);
              }}
              title="Exportar CSV"
            >
              <i className="tim-icons icon-cloud-download-93" />
            </Button>
          </div>
        );
      },
    },
  ];

  if (showForm) {
    return (
      <CotizacionForm
        key={formKey} // Key para forzar remontaje cuando cambia
        sucursalId={parseInt(sucursalSeleccionada)}
        usuarioId={user?.UsuarioID || user?.usuarioID}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  if (showDetalle && cotizacionSeleccionada) {
    return (
      <CotizacionDetalle
        cotizacion={cotizacionSeleccionada}
        onClose={handleDetalleClose}
        usuarioId={user?.UsuarioID || user?.usuarioID}
      />
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Cotizaciones</CardTitle>
                </Col>
                <Col className="text-right">
                  <Button 
                    color="success" 
                    onClick={exportarCotizacionesCSV} 
                    disabled={!cotizaciones || cotizaciones.length === 0}
                    className="mr-2"
                  >
                    <i className="tim-icons icon-cloud-download-93" /> Exportar CSV
                  </Button>
                  <Button color="primary" onClick={handleCreate} disabled={!sucursalSeleccionada}>
                    <i className="tim-icons icon-simple-add" /> Nueva Cotización
                  </Button>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="3">
                  <select
                    className="form-control"
                    value={sucursalSeleccionada}
                    onChange={(e) => setSucursalSeleccionada(e.target.value)}
                    disabled={loadingSucursales}
                  >
                    <option value="">Seleccione una sucursal</option>
                    {sucursales.map((suc) => (
                      <option key={suc.SucursalID} value={suc.SucursalID}>
                        {suc.Nombre}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col md="2">
                  <Input
                    type="text"
                    placeholder="Cliente ID"
                    value={filtroCliente}
                    onChange={(e) => setFiltroCliente(e.target.value)}
                  />
                </Col>
                <Col md="2">
                  <Input
                    type="date"
                    placeholder="Fecha desde"
                    value={filtroFechaDesde}
                    onChange={(e) => setFiltroFechaDesde(e.target.value)}
                  />
                </Col>
                <Col md="2">
                  <Input
                    type="date"
                    placeholder="Fecha hasta"
                    value={filtroFechaHasta}
                    onChange={(e) => setFiltroFechaHasta(e.target.value)}
                  />
                </Col>
                <Col md="2">
                  <Input
                    type="select"
                    value={filtroEstatus}
                    onChange={(e) => setFiltroEstatus(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="Completada">Completada</option>
                    <option value="Cancelada">Cancelada</option>
                  </Input>
                </Col>
                <Col md="1">
                  <Button color="primary" onClick={loadVentas} disabled={!sucursalSeleccionada}>
                    <i className="tim-icons icon-zoom-split" />
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={cotizaciones} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

    </div>
  );
}

export default Cotizaciones;

